import { Position } from "geojson";

import { MuPowerOutputLayerIds, MuPowerSolverLayerIds } from "fond/types";

import { ValidationFunction, ValidationHelpers } from "./GenericValidator";

/**
 * A position is a valid loop connection endpoint if it sits on a source hub, a transformer hub, or a
 * primary joint box (a feature in the boxes layer whose TYPE is "Primary Joint Box").
 */
const isAllowedEndpoint = (position: Position, helpers: ValidationHelpers): boolean =>
  helpers
    .findFeaturesAtPosition(position, [MuPowerSolverLayerIds.sourceHubs, MuPowerSolverLayerIds.transformerHubs, MuPowerOutputLayerIds.boxes])
    .some((f) => f.properties?.layerId !== MuPowerOutputLayerIds.boxes || f.properties?.TYPE === "Primary Joint Box");

/**
 * A loop connection is valid if its first and last vertices each sit on a source hub, transformer, or primary joint box.
 */
export const validateLoopConnection: ValidationFunction = (feature, _allFeatures, helpers) => {
  const featureId = feature.id as string;

  if (feature.geometry.type !== "LineString") {
    return { featureId, valid: false, errors: ["Loop connection must be a LineString"] };
  }

  const coords = feature.geometry.coordinates;
  if (coords.length < 2) {
    return { featureId, valid: false, errors: ["Loop connection must have at least 2 vertices"] };
  }

  const firstVertex = coords[0];
  const lastVertex = coords[coords.length - 1];

  const errors: string[] = [];
  if (!isAllowedEndpoint(firstVertex, helpers)) errors.push("Loop connection must start at a source hub, transformer, or primary joint box");
  if (!isAllowedEndpoint(lastVertex, helpers)) errors.push("Loop connection must end at a source hub, transformer, or primary joint box");

  return { featureId, valid: errors.length === 0, errors };
};
